"use client";

import { useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslations } from "next-intl";
import { Num } from "@/components/ui/Num";
import { cn, telHrefMa } from "@/lib/utils";
import { track } from "@/lib/observability/analytics";
import type { PhysicianTier } from "@/lib/data/physician-tier";

/**
 * Bouton "Appeler" des fiches sans RDV en ligne. Deux modes :
 *
 * - Praticien inscrit (cf. isRegisteredOnSahha) : lien tel: direct. Sur
 *   ordinateur, le numéro s'affiche à côté du libellé, sauf si l'appelant
 *   l'affiche déjà (`phoneVisible`).
 * - Sinon : modale explicative au lieu de déclencher `tel:` directement :
 *   - explique que le médecin n'est pas encore inscrit sur Sahha
 *   - affiche le numéro de téléphone (lien tel: dans la modale)
 *   - invite gentiment le patient à recommander Sahha au médecin pour faciliter
 *     les prochains RDV
 *
 * Réutilise les clés i18n `booking.flow.unclaimed.dialog*` déjà traduites FR/AR/EN.
 *
 * Funnel tracking complet (2026-05-09) :
 *   1. call_intent     — clic sur le bouton trigger → modal s'ouvre
 *   2. contact_phone   — clic sur le tel: dans la modal (bottom funnel)
 *   3. call_dismissed  — modal fermée SANS clic sur tel: (signal friction)
 * En mode direct, un clic émet 1 puis 2 : mêmes événements, mêmes sources.
 *
 * Le `source` permet de distinguer où le bouton est utilisé (search card,
 * fiche physician booking unavailable, profil mobile sticky, etc.) pour
 * comparer les taux de conversion par contexte.
 */
type Source =
  | "search_card"
  | "physician_page_unavailable"
  | "physician_page_mobile_sticky"
  | "physician_page_other"
  | "unspecified";

/**
 * Praticien inscrit sur Sahha : fiche rattachée à un compte ou revendiquée, ou
 * abonné Gold/Premium. Bug 2026-10-05 (bilan CONV-3) : la fenêtre « pas encore
 * inscrit » s'affichait sur les 81 fiches qui ont un compte, dont 75 Gold. Le
 * composant ne recevait que le tier, et « free » mêle les comptes réels et
 * les fiches VERIFIED importées sans compte.
 */
export function isRegisteredOnSahha({
  isClaimed,
  tier,
}: {
  isClaimed?: boolean;
  tier?: PhysicianTier;
}): boolean {
  return isClaimed === true || tier === "gold" || tier === "premium";
}

const PHONE_ICON = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 2.07 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.79a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.89.35 1.83.59 2.79.72A2 2 0 0 1 22 16.92Z"/>
  </svg>
);

export function UnclaimedCallButton({
  phone,
  className,
  children,
  ariaLabel,
  iconOnly = false,
  source = "unspecified",
  physicianSlug,
  tier,
  trialBoosted,
  city,
  isClaimed = false,
  phoneVisible = false,
}: {
  phone: string;
  className?: string;
  children?: React.ReactNode;
  ariaLabel?: string;
  iconOnly?: boolean;
  /** Contexte d'affichage du bouton — utile pour comparer les conversions par surface. */
  source?: Source;
  /** Slug du physician — permet de relier les events au profil dans GA4 / Plausible. */
  physicianSlug?: string;
  /** Tier réel du médecin (gold/premium/free/unclaimed) — analytics + choix du mode. */
  tier?: PhysicianTier;
  /** Tier de placement offert (3 mois gratuits) si applicable — permet
   *  d'isoler dans les analyses l'impact des placements vs les abonnés réels. */
  trialBoosted?: "gold" | "premium";
  /** Ville du médecin — segmentation analytics. */
  city?: string;
  /** Fiche rattachée à un compte (userId) ou revendiquée (claimedAt). */
  isClaimed?: boolean;
  /** Mode direct : le numéro est déjà visible (libellé ou texte voisin). */
  phoneVisible?: boolean;
}) {
  const t = useTranslations("booking.flow");
  const [open, setOpen] = useState(false);
  // Track si l'utilisateur a déjà cliqué sur tel: pendant cette ouverture du dialog —
  // évite de double-tracker `call_dismissed` quand la fermeture est due à la nav tel:.
  const phoneClickedRef = useRef(false);

  // PHONE_CLICK (DB TrackEvent) via sendBeacon — fiable même si la page navigue
  // (tel: / dialer). Fire dès l'ouverture de la modale (= clic "Appeler le
  // cabinet") : c'est l'interaction téléphone que le médecin attend de voir
  // dans ses stats. On ne le re-fire PAS sur le tel: interne (pas de double).
  const firePhoneClick = () => {
    const payload = JSON.stringify({
      type: "PHONE_CLICK",
      entityType: "Physician",
      entitySlug: physicianSlug,
      source: `call-cabinet:${source}`,
      metadata: { tier: tier ?? null, trialBoosted: trialBoosted ?? null, city: city ?? null },
    });
    try {
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        const ok = navigator.sendBeacon("/api/track/event", new Blob([payload], { type: "application/json" }));
        if (ok) return;
      }
      fetch("/api/track/event", { method: "POST", headers: { "content-type": "application/json" }, body: payload, keepalive: true, credentials: "same-origin" }).catch(() => {});
    } catch { /* ignore */ }
  };

  // Bottom-funnel : tel: cliqué effectivement = CONVERSION.
  // 1) Plausible/GA4 via track() (clic = "physician:contact_phone")
  // 2) DB via sendBeacon avec source prefixee "call-completed:*"
  //    pour distinguer du PHONE_CLICK initial (modal open INTENT).
  //    Permet au dashboard /admin/analytics/evenements de calculer
  //    le funnel intent -> conversion (~30-40% typique).
  const fireCallCompleted = () => {
    track("physician:contact_phone", {
      source,
      physician_slug: physicianSlug,
      tier,
      trial_boosted: trialBoosted,
      city,
    });
    try {
      const completedPayload = JSON.stringify({
        type: "PHONE_CLICK",
        entityType: "Physician",
        entitySlug: physicianSlug,
        source: `call-completed:${source}`,
        metadata: { tier: tier ?? null, trialBoosted: trialBoosted ?? null, city: city ?? null },
      });
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon("/api/track/event", new Blob([completedPayload], { type: "application/json" }));
      }
    } catch {
      // ignore : navigation tel: doit toujours passer
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      // Modal s'ouvre — track l'intent + PHONE_CLICK (DB) pour les stats médecin
      phoneClickedRef.current = false;
      firePhoneClick();
      track("physician:call_intent", {
        source,
        physician_slug: physicianSlug,
      });
    } else if (!phoneClickedRef.current) {
      // Modal se ferme SANS qu'on ait cliqué sur tel: — friction
      track("physician:call_dismissed", {
        source,
        physician_slug: physicianSlug,
      });
    }
  };

  if (isRegisteredOnSahha({ isClaimed, tier })) {
    return (
      <a
        href={telHrefMa(phone)}
        aria-label={ariaLabel}
        data-event="physician_call_intent"
        data-source={source}
        className={className}
        onClick={() => {
          firePhoneClick();
          track("physician:call_intent", {
            source,
            physician_slug: physicianSlug,
          });
          fireCallCompleted();
        }}
      >
        {iconOnly ? PHONE_ICON : children}
        {!iconOnly && !phoneVisible ? (
          <Num className="hidden font-mono lg:inline">{phone}</Num>
        ) : null}
      </a>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          data-event="physician_call_intent"
          data-source={source}
          className={className}
        >
          {iconOnly ? PHONE_ICON : children}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] bg-[rgb(15_30_58_/_0.5)] backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby="unclaimed-call-desc"
          className={cn(
            "fixed inset-x-3 bottom-3 top-auto z-[90] mx-auto max-w-md rounded-[24px] bg-white p-5 pb-6 shadow-2xl",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4",
            "sm:inset-x-auto sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:-translate-x-1/2 sm:-translate-y-1/2",
          )}
        >
          <div className="text-center">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--sahha-primary-50)] text-[var(--sahha-primary-700)]">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" strokeLinejoin="round"/>
              </svg>
            </span>
            <Dialog.Title className="mt-3 text-[17px] font-extrabold text-[var(--sahha-text-900)]">
              {t("unclaimed.dialogTitle")}
            </Dialog.Title>
            <Dialog.Description id="unclaimed-call-desc" className="mt-2 text-[13.5px] leading-snug text-[var(--sahha-text-700)]">
              {t("unclaimed.dialogBody")}
            </Dialog.Description>
          </div>

          <a
            href={telHrefMa(phone)}
            data-event="physician_contact_phone"
            data-source={source}
            onClick={() => {
              phoneClickedRef.current = true;
              fireCallCompleted();
            }}
            className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--sahha-accent-500)] text-[13.5px] font-extrabold uppercase tracking-wide text-white shadow-[0_3px_10px_-3px_rgba(244,162,97,0.45)] transition-all active:scale-[0.98] hover:bg-[var(--sahha-accent-600)]"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"/>
            </svg>
            <span>{t("unclaimed.dialogCallBtn")}</span>
            <Num className="font-mono">{phone}</Num>
          </a>

          <div className="mt-4 rounded-2xl bg-[var(--sahha-primary-50)] p-4 text-center">
            <p className="text-[14px] font-extrabold text-[var(--sahha-primary-800)]">
              {t("unclaimed.dialogHelpTitle")}
            </p>
            <p className="mt-1 text-[12.5px] leading-snug text-[var(--sahha-primary-700)]">
              {t("unclaimed.dialogHelpBody")}
            </p>
          </div>

          <Dialog.Close asChild>
            <button
              type="button"
              data-event="physician_call_dismissed"
              data-source={source}
              className="mt-3 h-10 w-full rounded-xl bg-white text-[13px] font-bold text-[var(--sahha-text-700)] ring-1 ring-[var(--sahha-border-default)] transition active:scale-[0.98] hover:bg-[var(--sahha-surface-1)]"
            >
              {t("unclaimed.dialogClose")}
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
